import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { useSearchParams } from 'react-router-dom';
import {
  MapPin, Shield, Users, ChevronRight,
  CheckCircle, Lock, Mail, Building2, Phone, Star, AlertCircle, MapPinned
} from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_15d8202d-8a04-4d5a-9567-0f60354c3cff/artifacts/qenr8rph_CYQR_Gold.png';

// 10 wards only — Mayor is a separate backend entry, not sold as a ward placement
const WARDS = [
  { id: 'Ward 1',  label: 'Ward 1' },
  { id: 'Ward 2',  label: 'Ward 2' },
  { id: 'Ward 3',  label: 'Ward 3' },
  { id: 'Ward 4',  label: 'Ward 4' },
  { id: 'Ward 5',  label: 'Ward 5' },
  { id: 'Ward 6',  label: 'Ward 6' },
  { id: 'Ward 7',  label: 'Ward 7' },
  { id: 'Ward 8',  label: 'Ward 8' },
  { id: 'Ward 9',  label: 'Ward 9' },
  { id: 'Ward 10', label: 'Ward 10' },
];

const STANDARD_FEATURES = [
  'Exclusive ward placement — only 1 realtor per ward',
  "Featured on every resident's personal ward dashboard",
  "Full profile on your ward's public page",
  'Photo, bio, contact info, and call to action button',
  'Active for 12 full months — no mid-term changes',
  'Right of first refusal 60 days before expiry of your current placement. If confirmation of intent to renew is not received within 30 days of notice, the ward will be made available to other potential agents.',
];

const ELITE_FEATURES = [
  'All features from the Standard tier',
  'Co-branded "Ward Realtor" badge for social sharing',
  'Priority support and dedicated account contact',
];

const Realtors = () => {
  const [takenWards, setTakenWards]     = useState({});
  const [selectedWard, setSelectedWard] = useState('');
  const [loading, setLoading]           = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(true);
  const [searchParams]                  = useSearchParams();
  const [form, setForm]                 = useState({ name: '', email: '', phone: '', brokerage: '' });

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      const ward = searchParams.get('ward') || 'your ward';
      toast.success(`Payment received! Your exclusive placement on ${ward} is being activated. We'll be in touch within 24 hours.`);
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout canceled — no charges were made.');
    }
  }, [searchParams]);

  useEffect(() => {
    api.get('/billing/ward-availability')
      .then(r => setTakenWards(r.data.taken || {}))
      .catch(() => {})
      .finally(() => setLoadingAvail(false));
  }, []);

  const isTaken = (id) => !!takenWards[id];
  const availableCount = WARDS.filter(w => !isTaken(w.id)).length;

  const handleCheckout = async () => {
    if (!selectedWard)             return toast.error('Select a ward first');
    if (!form.name || !form.email) return toast.error('Name and email are required');
    setLoading(true);
    try {
      const res = await api.post(
        `/billing/start-realtor?ward=${encodeURIComponent(selectedWard)}&plan=yearly` +
        `&realtor_name=${encodeURIComponent(form.name)}&realtor_email=${encodeURIComponent(form.email)}` +
        `&brokerage=${encodeURIComponent(form.brokerage)}`
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#0D0D0D] text-white">

        {/* HERO */}
        <div className="relative min-h-[85vh] flex items-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-[#0D0D0D]/40" />

          <div className="relative w-full max-w-7xl mx-auto px-8 py-32">
            <div className="flex items-center gap-2 text-[#C5A52A] font-mono text-xs uppercase tracking-[0.3em] mb-8">
              <MapPin className="w-3 h-3" />
              <span>CYQR.CA · Regina Civic Platform · Realtor Program</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black leading-none mb-6 tracking-tight">
              Own Your<br />
              <span style={{ color: '#C5A52A' }}>Ward.</span>
            </h1>
            <p className="text-gray-300 text-xl md:text-2xl max-w-2xl mb-10 leading-relaxed font-light">
              Exclusive digital territory in Regina's fastest-growing civic platform.
              One realtor. One ward. One year.
              Your profile in front of every resident who cares enough to show up.
            </p>
            <div className="flex flex-wrap gap-8 mb-12">
              {[
                { icon: <Shield className="w-4 h-4" style={{ color: '#C5A52A' }} />, text: '100% Ward Exclusive' },
                { icon: <Users className="w-4 h-4" style={{ color: '#C5A52A' }} />, text: 'Engaged Civic Audience' },
                { icon: <Star  className="w-4 h-4" style={{ color: '#C5A52A' }} />, text: 'Year-Round Visibility' },
              ].map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-gray-300 text-sm">
                  {icon}<span className="font-mono">{text}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#claim"
                className="inline-flex items-center gap-2 px-10 py-5 font-black uppercase tracking-widest text-base transition-all"
                style={{ background: '#C5A52A', color: '#0D0D0D' }}
                onMouseEnter={e => e.currentTarget.style.background = '#d4b23a'}
                onMouseLeave={e => e.currentTarget.style.background = '#C5A52A'}
              >
                Claim Your Ward <ChevronRight className="w-5 h-5" />
              </a>
              <div
                className="inline-flex items-center gap-2 px-6 py-5 border text-sm font-mono uppercase tracking-widest"
                style={{ borderColor: '#C5A52A33', color: '#C5A52A' }}
              >
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#C5A52A' }} />
                {loadingAvail ? '...' : `${availableCount} of 10 wards available`}
              </div>
            </div>
          </div>
        </div>

        {/* WHY IT WORKS */}
        <div className="border-y border-[#C5A52A]/10 bg-[#111111]">
          <div className="max-w-7xl mx-auto px-8 py-20">
            <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-[0.3em] text-center mb-3">Why It Works</p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-4">Your Territory. Your Audience.</h2>
            <p className="text-gray-500 text-center text-base max-w-2xl mx-auto mb-16">
              CYQR users are registered by postal code to their specific ward. They're not passive scrollers —
              they're engaged residents actively participating in their neighbourhood.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#C5A52A]/10">
              {[
                {
                  accent: '100% Local',
                  title:  'Hyperlocal by Design',
                  body:   'Every user is mapped to their ward at registration. Your ad reaches only the residents in your territory — zero wasted impressions.',
                },
                {
                  accent: '0 Competitors',
                  title:  'True Exclusivity',
                  body:   'One realtor per ward. Period. Once your ward is claimed, no competitor can advertise there for the full term.',
                },
                {
                  accent: 'Always Visible',
                  title:  'Prime Placement',
                  body:   "Your profile appears on the ward's public page and on every resident's personal dashboard — always above the fold.",
                },
              ].map(item => (
                <div key={item.title} className="bg-[#111111] p-10 hover:bg-[#161611] transition-colors">
                  <p className="font-black text-4xl mb-4 font-mono" style={{ color: '#C5A52A' }}>{item.accent}</p>
                  <h3 className="text-white font-bold text-xl mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-base leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PRICING */}
        <div className="max-w-7xl mx-auto px-8 py-24">
          <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-[0.3em] text-center mb-3">Investment Tiers</p>
          <h2 className="text-4xl md:text-5xl font-black text-center mb-4">Select Your Level</h2>
          <p className="text-gray-600 text-center font-mono text-sm mb-16">
            All fees are subject to GST (5%) and PST (6%) · Prices in Canadian dollars
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* STANDARD */}
            <div className="border border-[#C5A52A]/40 p-10 relative bg-[#111111] hover:border-[#C5A52A] transition-colors">
              <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, #C5A52A, transparent)' }} />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C5A52A] mb-4">Exclusive Agent</p>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-6xl md:text-7xl font-black" style={{ color: '#C5A52A' }}>$1,500</span>
                <span className="text-gray-500 mb-3 font-mono text-lg">/year</span>
              </div>
              <p className="text-gray-600 text-sm font-mono mb-10">One ward. One year. One realtor.</p>

              <ul className="space-y-5 mb-10">
                {STANDARD_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-3 text-base text-gray-300 leading-relaxed">
                    <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#C5A52A' }} />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 text-sm font-mono">Select your ward below to claim it ↓</p>
            </div>

            {/* ELITE */}
            <div className="border border-[#C5A52A]/20 p-10 relative bg-[#0F0F0A] hover:border-[#C5A52A]/50 transition-colors">
              <div className="absolute top-0 left-0 right-0 h-0.5 opacity-30" style={{ background: 'linear-gradient(90deg, #C5A52A, transparent)' }} />
              <div className="flex items-start justify-between mb-4">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-400">MLS Elite</p>
                <span className="text-xs font-black px-2 py-1 font-mono uppercase tracking-wider" style={{ background: '#C5A52A', color: '#0D0D0D' }}>
                  By Application
                </span>
              </div>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-6xl md:text-7xl font-black text-gray-300">$5,000</span>
                <span className="text-gray-600 mb-3 font-mono text-lg">/year</span>
              </div>
              <p className="text-[#C5A52A]/60 text-sm font-mono mb-8">
                First installment of $2,500 due upon reservation · MLS API key required
              </p>

              <ul className="space-y-5 mb-8">
                {ELITE_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-3 text-base text-gray-400 leading-relaxed">
                    <CheckCircle className="w-5 h-5 mt-0.5 shrink-0 text-gray-600" />
                    {f}
                  </li>
                ))}
              </ul>

              {/* Geo-Badge Feature */}
              <div className="border border-[#C5A52A]/30 bg-[#C5A52A]/5 p-6 mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <MapPinned className="w-5 h-5" style={{ color: '#C5A52A' }} />
                  <p className="text-[#C5A52A] font-black uppercase tracking-wider text-sm">Exclusive Engagement Feature</p>
                </div>
                <p className="text-white font-bold text-lg mb-3">1 Premium Geo-Badge per Year</p>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Deploy a custom high-value badge at a location of your choice — an open house, listing event, or showing.
                </p>
                <ul className="space-y-2 mb-4">
                  {[
                    'Residents who check in unlock the badge',
                    'Badge carries elevated point value within CYQR',
                    'Drives real-world foot traffic and digital engagement',
                    'Creates a gamified incentive for people to physically show up',
                  ].map(pt => (
                    <li key={pt} className="flex items-start gap-2 text-sm text-gray-400">
                      <span style={{ color: '#C5A52A' }} className="mt-0.5 shrink-0">→</span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-500 text-sm italic leading-relaxed">
                  You're not just advertising. You're pulling people into your listings.
                </p>
              </div>

              {/* Legal conditions */}
              <div className="border border-yellow-900/40 bg-yellow-950/20 p-5 mb-8">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-yellow-700/80 space-y-2 leading-relaxed">
                    <p><strong className="text-yellow-600">Conditions of the MLS Elite package:</strong></p>
                    <p>• Activation is conditional upon the realtor obtaining and providing a valid MLS/IDX API key (such as a RETS or DDF access key issued through CREA or your local real estate board).</p>
                    <p>• API subscription costs are the responsibility of the realtor and are <strong>not</strong> included in this package.</p>
                    <p>• A first installment of <strong>$2,500 CAD</strong> is due upon reservation of the ward to secure your exclusive placement while your MLS API key application is in progress.</p>
                    <p>• The second installment of <strong>$2,500 CAD</strong> is due upon approval and confirmation of MLS API access.</p>
                    <p>• Should MLS API approval not occur, the realtor will be defaulted to the Exclusive Agent tier for the full term with no further payment required.</p>
                  </div>
                </div>
              </div>

              <a
                href="mailto:ads@accountability.works?subject=CYQR%20MLS%20Elite%20Realtor%20Inquiry"
                className="flex items-center justify-center gap-2 w-full py-4 border font-mono text-sm uppercase tracking-wider transition-colors"
                style={{ borderColor: '#C5A52A40', color: '#C5A52A' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#C5A52A15'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Mail className="w-4 h-4" />
                Contact Us to Reserve
              </a>
            </div>
          </div>
        </div>

        {/* CLAIM SECTION */}
        <div id="claim" className="border-t border-[#C5A52A]/10 bg-[#111111]">
          <div className="max-w-5xl mx-auto px-8 py-20">
            <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-[0.3em] text-center mb-3">Claim Your Ward</p>
            <h2 className="text-4xl md:text-5xl font-black text-center mb-4">Secure Your Territory</h2>
            <p className="text-gray-500 text-center text-base mb-12 font-mono">
              Select an available ward, fill in your details, and check out in minutes via Stripe.
            </p>

            <div className="bg-[#0D0D0D] border border-[#C5A52A]/20">

              {/* Step 1 */}
              <div className="p-8 border-b border-[#C5A52A]/10">
                <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-[0.2em] mb-6">Step 1 — Select Your Ward</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {WARDS.map(ward => {
                    const taken    = isTaken(ward.id);
                    const selected = selectedWard === ward.id;
                    return (
                      <button
                        key={ward.id}
                        onClick={() => { if (!taken) setSelectedWard(ward.id); }}
                        disabled={taken}
                        className="relative p-4 text-left border transition-all"
                        style={{
                          borderColor: taken ? '#1a1a1a' : selected ? '#C5A52A' : '#1E1E1E',
                          background:  taken ? '#0D0D0D'  : selected ? '#C5A52A10' : '#111111',
                          cursor:      taken ? 'not-allowed' : 'pointer',
                          opacity:     taken ? 0.4 : 1,
                        }}
                      >
                        {taken    && <Lock         className="absolute top-2 right-2 w-3 h-3 text-gray-700" />}
                        {selected && <CheckCircle  className="absolute top-2 right-2 w-3 h-3" style={{ color: '#C5A52A' }} />}
                        <p className="font-bold text-sm" style={{ color: taken ? '#444' : selected ? '#fff' : '#ccc' }}>
                          {ward.label}
                        </p>
                        <p className="font-mono text-xs mt-1" style={{ color: taken ? '#5a1a1a' : '#555' }}>
                          {taken ? `Claimed` : 'Available'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-8 border-b border-[#C5A52A]/10">
                <p className="text-[#C5A52A] font-mono text-xs uppercase tracking-[0.2em] mb-6">Step 2 — Your Information</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'name',      label: 'Full Name *',        placeholder: 'Jane Smith',              icon: <Users     className="w-4 h-4" /> },
                    { key: 'email',     label: 'Email Address *',    placeholder: 'jane@yourrealty.ca',      icon: <Mail      className="w-4 h-4" /> },
                    { key: 'brokerage', label: 'Brokerage / Team',   placeholder: 'Century 21 Dome Realty',  icon: <Building2 className="w-4 h-4" /> },
                    { key: 'phone',     label: 'Phone (optional)',    placeholder: '(306) 555-0100',          icon: <Phone     className="w-4 h-4" /> },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-gray-500 text-xs font-mono mb-2 uppercase tracking-widest">{field.label}</label>
                      <div className="relative">
                        <div className="absolute left-3 top-3 text-gray-700">{field.icon}</div>
                        <input
                          type="text"
                          value={form[field.key]}
                          onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                          placeholder={field.placeholder}
                          className="w-full bg-[#111111] border border-[#1E1E1E] text-white pl-10 pr-4 py-3 text-base outline-none transition-colors placeholder:text-gray-700 focus:border-[#C5A52A]/50"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 3: Checkout */}
              <div className="p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    {selectedWard ? (
                      <>
                        <p className="text-white font-bold text-lg">{selectedWard} — $1,500/year CAD</p>
                        <p className="text-gray-600 text-sm font-mono mt-0.5">
                          Processed securely via Stripe · Fees subject to GST (5%) + PST (6%)
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-600 font-mono">Select a ward above to continue</p>
                    )}
                  </div>
                  <button
                    onClick={handleCheckout}
                    disabled={loading || !selectedWard}
                    className="flex items-center gap-2 px-10 py-5 font-black uppercase tracking-widest text-base transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{
                      background: selectedWard ? '#C5A52A' : '#222',
                      color:      selectedWard ? '#0D0D0D' : '#555',
                    }}
                  >
                    {loading
                      ? <><span className="animate-spin inline-block">↻</span> Redirecting...</>
                      : <>Claim {selectedWard || 'Ward'} <ChevronRight className="w-5 h-5" /></>
                    }
                  </button>
                </div>
              </div>
            </div>

            {/* Trust row */}
            <div className="mt-6 grid grid-cols-3 gap-4 text-center border-t border-[#C5A52A]/10 pt-6">
              {[
                { label: 'Secure Checkout',       sub: 'via Stripe · 256-bit SSL' },
                { label: 'Instant Confirmation',  sub: 'activated within 24 hours' },
                { label: 'Right of First Refusal', sub: '60 days before expiry' },
              ].map(item => (
                <div key={item.label}>
                  <p className="text-gray-400 text-sm font-bold">{item.label}</p>
                  <p className="text-gray-700 text-xs font-mono mt-0.5">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="border-t border-[#C5A52A]/10">
          <div className="max-w-4xl mx-auto px-8 py-20">
            <h2 className="text-3xl font-black mb-12 text-center">Common Questions</h2>
            <div className="space-y-8">
              {[
                {
                  q: 'What does "exclusive" mean in practice?',
                  a: 'Once you claim a ward, no other realtor can advertise in that ward on CYQR for the duration of your term. Your profile is the only one shown to residents of that ward — on the public ward page and their personal dashboard.',
                },
                {
                  q: 'What happens when my year ends?',
                  a: 'You receive right of first refusal 60 days before expiry of your current placement. If confirmation of intent to renew is not received within 30 days of that notice, the ward will be made available to other potential agents.',
                },
                {
                  q: 'What is the MLS API key required for the Elite package?',
                  a: 'The MLS Elite tier integrates your active listings directly onto your ward page using a DDF or RETS API key issued through CREA or your local real estate board. Obtaining this key is the realtor\'s responsibility, and subscription fees for API access are not included in the CYQR package price.',
                },
                {
                  q: 'What happens if my MLS API key application is not approved?',
                  a: 'If MLS API approval does not occur, you will be defaulted to the Exclusive Agent tier for the full term at no additional cost. Your first installment of $2,500 secures your ward exclusively while the application is in progress.',
                },
                {
                  q: 'How does the Premium Geo-Badge work?',
                  a: 'As an MLS Elite member, you get one custom geo-badge per year that you can deploy at a physical location — an open house, listing event, or showing. Residents who visit the location and check in through CYQR unlock the badge and earn elevated points. It creates real-world foot traffic with a digital engagement layer built in.',
                },
                {
                  q: 'Are there taxes on top of the listed prices?',
                  a: 'Yes. All fees are subject to Saskatchewan GST (5%) and PST (6%), for a combined tax of 11%. This will be reflected at checkout.',
                },
              ].map(faq => (
                <div key={faq.q} className="border-b border-[#1E1E1E] pb-8">
                  <p className="text-white font-bold text-lg mb-2">{faq.q}</p>
                  <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Realtors;
